import { useState } from "react";
import {
  Box,
  Fade,
  IconButton,
  Paper,
  Stack,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import {
  CalendarMonth,
  Close,
  OpenInNew,
  ContentCopy,
  Share,
  Check,
} from "@mui/icons-material";

export default function BookingConfirmCard({ booking, onClose }) {
  const [copied, setCopied] = useState(false);

  // Check if native sharing is available in the current browser/device context
  const isShareSupported = typeof navigator !== "undefined" && !!navigator.share;

  const handleAction = async () => {
    if (!booking?.eventUrl) return;

    if (isShareSupported) {
      try {
        await navigator.share({
          title: "Booking Confirmed",
          text: `Booking Code: ${booking.bookingCode}`,
          url: booking.eventUrl,
        });
      } catch (err) {
        // Handle gracefully if the user cancels or closes the native share tray
        if (err.name !== "AbortError") {
          console.error("Error sharing: ", err);
        }
      }
    } else {
      // Fallback copy logic for unsupported browsers
      try {
        await navigator.clipboard.writeText(booking.eventUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy link: ", err);
      }
    }
  };

  return (
    <Fade in>
      <Paper
        elevation={2}
        sx={{
          mx: 2,
          mb: 2,
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "success.light",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #062f19 0%, #022c16 100%)"
              : "linear-gradient(135deg, #f0fdf4 0%, #e6f9ed 100%)",
        }}
      >
        {/* Header Block */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <CalendarMonth color="success" sx={{ fontSize: 22 }} />
              <Typography variant="subtitle1" fontWeight={700} color="success.dark">
                Booking Confirmed!
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Booking Code:{" "}
              <Box component="span" fontWeight={600} color="text.primary">
                {booking.bookingCode}
              </Box>
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -0.5, color: "text.secondary" }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {/* Conditional Footer Actions */}
        {booking?.eventUrl && (
          <Stack
            direction="row"
            spacing={1.5}
            mt={2}
            pt={1.5}
            sx={{ borderTop: "1px solid", borderColor: "success.200" }}
          >
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              href={booking.eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "success.50",
                },
              }}
            >
              Open Link
            </Button>

            <Tooltip title={copied ? "Copied!" : isShareSupported ? "Share Link" : "Copy Link"} arrow placement="top">
              <Button
                variant="text"
                color="success"
                size="small"
                startIcon={
                  copied ? (
                    <Check sx={{ fontSize: 16 }} />
                  ) : isShareSupported ? (
                    <Share sx={{ fontSize: 16 }} />
                  ) : (
                    <ContentCopy sx={{ fontSize: 16 }} />
                  )
                }
                onClick={handleAction}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {copied ? "Copied" : isShareSupported ? "Share" : "Copy"}
              </Button>
            </Tooltip>
          </Stack>
        )}
      </Paper>
    </Fade>
  );
}